import React, { useState, useEffect } from 'react';
import { User, Mail, Shield, Smartphone, Camera, Save, LogOut, X, Loader, Webhook, ArrowLeft, ChevronRight, Lock, CheckCircle2, AlertTriangle, Link2, Eye, EyeOff, ShieldCheck, AlertCircle } from 'lucide-react';
import api from '../services/api';
import Webhooks from './Webhooks';

function Profile({ user, onStatusMessage, onUserUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [fullName, setFullName] = useState(user?.fullName || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.fullName !== undefined) {
      setFullName(user?.fullName || '');
    }
  }, [user?.fullName]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showWebhooks, setShowWebhooks] = useState(false);

  // Dedicated Email update modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailModalError, setEmailModalError] = useState('');

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdModalError, setPwdModalError] = useState('');

  const pwdRules = {
    length: newPassword.length >= 8,
    upper: /[A-Z]/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[^A-Za-z0-9]/.test(newPassword)
  };
  const isPwdValid = Object.values(pwdRules).every(Boolean);
  const doPwdsMatch = Boolean(newPassword && confirmPassword && newPassword === confirmPassword);

  const handleOpenPasswordModal = () => {
    setNewPassword('');
    setConfirmPassword('');
    setPwdModalError('');
    setShowPassword(false);
    setShowConfirm(false);
    setShowPasswordModal(true);
  };

  // Git Providers / Linked VCS state
  const [gitProvidersStatus, setGitProvidersStatus] = useState({
    github: false,
    gitlab: false,
    githubDetails: null,
    gitlabDetails: null
  });
  const [gitActionLoading, setGitActionLoading] = useState(false);
  const [gitActionError, setGitActionError] = useState('');
  const [gitActionSuccess, setGitActionSuccess] = useState('');

  const loadGitProvidersStatus = async () => {
    try {
      const data = await api.getGitProvidersStatus();
      if (data && data.providers) {
        setGitProvidersStatus(data.providers);
      }
    } catch (e) {
      console.warn('[Profile] Failed to load git provider status:', e.message);
    }
  };

  useEffect(() => {
    loadGitProvidersStatus();

    const onOAuthMessage = (event) => {
      // Security: Validate message origin
      const allowedOrigins = [
        window.location.origin,
        process.env.REACT_APP_API_BASE_URL ? new URL(process.env.REACT_APP_API_BASE_URL, window.location.origin).origin : null
      ].filter(Boolean);
      if (!allowedOrigins.includes(event.origin)) {
        return;
      }

      if (event.data && event.data.type === 'git-oauth-result') {
        if (event.data.success) {
          const prov = String(event.data.provider || '').toLowerCase();
          setGitActionSuccess(`${prov} connected successfully!`);
          setGitActionError('');
          if (prov === 'github' || prov === 'gitlab') {
            setGitProvidersStatus((prev) => ({
              ...prev,
              [prov]: true
            }));
          }
          loadGitProvidersStatus();
        } else {
          setGitActionError(event.data.message || 'OAuth connection failed');
        }
      }
    };
    window.addEventListener('message', onOAuthMessage);
    return () => window.removeEventListener('message', onOAuthMessage);
  }, []);

  const handleConnectProvider = async (provider) => {
    setGitActionError('');
    setGitActionSuccess('');
    setGitActionLoading(true);
    try {
      const data = await api.connectGitProvider(provider);
      if (data && data.authUrl) {
        const popup = window.open(data.authUrl, 'git-oauth-popup', 'width=680,height=760');
        if (!popup) {
          setGitActionError('Popup blocked. Please allow popups and try again.');
        }
      }
    } catch (err) {
      setGitActionError(err.message || `Failed to initiate connection with ${provider}`);
    } finally {
      setGitActionLoading(false);
    }
  };

  const handleDisconnectProvider = async (provider) => {
    if (!window.confirm(`Are you sure you want to disconnect ${provider}?`)) return;
    setGitActionError('');
    setGitActionSuccess('');
    setGitActionLoading(true);
    try {
      await api.disconnectGitProvider(provider);
      setGitActionSuccess(`${provider} disconnected successfully.`);
      await loadGitProvidersStatus();
    } catch (err) {
      setGitActionError(err.message || `Failed to disconnect ${provider}`);
    } finally {
      setGitActionLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const payload = { fullName: fullName ? fullName.trim() : null };
      await api.updateMyProfile(payload);
      setMessage('Profile updated successfully!');
      setIsEditing(false);

      const localUser = JSON.parse(localStorage.getItem('user') || '{}');
      localUser.fullName = fullName ? fullName.trim() : null;
      localStorage.setItem('user', JSON.stringify(localUser));

      if (onUserUpdate) {
        onUserUpdate({ fullName: fullName ? fullName.trim() : null });
      }

      if (onStatusMessage) {
        onStatusMessage('Profile updated successfully', 'success');
      }
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwdLoading) return;

    if (!isPwdValid) {
      setPwdModalError('Please fulfill all password security requirements.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdModalError('Passwords do not match. Please re-enter.');
      return;
    }

    setPwdLoading(true);
    setPwdModalError('');
    setError('');
    setMessage('');

    try {
      await api.updateMyProfile({ password: newPassword });
      setMessage(user?.passwordSet ? 'Password updated successfully!' : 'Password set successfully! Your account is now secured.');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setGitActionError('');

      // Update stored session so passwordSet becomes true
      const localUser = JSON.parse(localStorage.getItem('user') || '{}');
      localUser.passwordSet = true;
      localStorage.setItem('user', JSON.stringify(localUser));
      if (api.user) api.user.passwordSet = true;
      if (onUserUpdate) {
        onUserUpdate({ passwordSet: true });
      }
    } catch (err) {
      setPwdModalError(err.message || 'Failed to update password');
    } finally {
      setPwdLoading(false);
    }
  };

  const handleOpenEmailModal = () => {
    setNewEmail('');
    setEmailModalError('');
    setShowEmailModal(true);
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (emailLoading) return;

    const trimmed = (newEmail || '').trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmed || !emailRegex.test(trimmed)) {
      setEmailModalError('Please enter a valid email address.');
      return;
    }
    if (trimmed.toLowerCase() === (user?.email || '').toLowerCase()) {
      setEmailModalError('New email must be different from current email.');
      return;
    }

    setEmailLoading(true);
    setEmailModalError('');
    setError('');
    setMessage('');

    try {
      const result = await api.updateMyProfile({ email: trimmed });
      if (result && result.token) {
        localStorage.setItem('token', result.token);
      }

      const localUser = JSON.parse(localStorage.getItem('user') || '{}');
      localUser.email = trimmed;
      localStorage.setItem('user', JSON.stringify(localUser));

      if (api.user) api.user.email = trimmed;
      if (onUserUpdate) onUserUpdate({ email: trimmed });

      setMessage('Email address updated successfully! A security notice was sent to your inbox.');
      if (onStatusMessage) onStatusMessage('Email updated successfully', 'success');
      setShowEmailModal(false);
      setNewEmail('');
    } catch (err) {
      setEmailModalError(err.message || 'Failed to update email address.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleLogout = () => {
    api.logout();
    window.location.href = '/login';
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto', transition: 'max-width 0.3s ease-out', animation: 'fadeIn 0.5s ease-out' }}>
      {!showWebhooks ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <div>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--text-main)', margin: '0 0 0.5rem 0' }}>Account Settings</h2>
              <p style={{ color: 'var(--text-muted)', margin: 0 }}>Manage your profile details and security preferences.</p>
            </div>
            <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: 'var(--danger-light, rgba(239,68,68,0.1))', color: 'var(--danger, #ef4444)', border: '1px solid transparent', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', transition: 'all 0.2s' }}>
              <LogOut size={16} /> Sign out all devices
            </button>
          </div>

          {message && <div style={{ padding: '1rem', background: 'rgba(16,185,129,0.1)', color: 'var(--success, #10b981)', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid rgba(16,185,129,0.2)' }}>{message}</div>}
          {error && <div style={{ padding: '1rem', background: 'rgba(239,68,68,0.1)', color: 'var(--danger, #ef4444)', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid rgba(239,68,68,0.2)' }}>{error}</div>}

          {/* Main Profile Info Card */}
          <div style={{ background: 'var(--surface)', borderRadius: '16px', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: '2rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
            <div style={{ padding: '2rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                <div style={{ position: 'relative', cursor: 'pointer' }}>
                  <div style={{ width: '96px', height: '96px', borderRadius: '50%', background: 'var(--primary-light, rgba(59,130,246,0.1))', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    <User size={48} color="var(--primary)" />
                  </div>
                  <div style={{ position: 'absolute', bottom: 0, right: 0, background: 'var(--surface-2)', border: '1px solid var(--border)', padding: '0.5rem', borderRadius: '50%', color: 'var(--text-main)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <Camera size={16} />
                  </div>
                </div>
                <div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-main)', margin: '0 0 0.25rem 0' }}>
                    {fullName || user?.fullName || user?.username || 'User'}
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.2rem' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>@{user?.username}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', background: 'var(--surface-2)', padding: '0.1rem 0.5rem', borderRadius: '12px', border: '1px solid var(--border)', textTransform: 'capitalize' }}>
                      {user?.role || 'Developer'}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsEditing(!isEditing);
                  if (!isEditing) {
                    setFullName(user?.fullName || '');
                    setError('');
                    setMessage('');
                  }
                }}
                style={{ padding: '0.5rem 1.25rem', background: isEditing ? 'var(--surface-2)' : 'var(--primary)', color: isEditing ? 'var(--text-main)' : 'white', border: isEditing ? '1px solid var(--border)' : 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s' }}>
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </button>
            </div>

            <div style={{ padding: '2rem', display: 'grid', gap: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Full Name */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: '600' }}>Full Name</label>
                  <input
                    disabled={!isEditing}
                    value={isEditing ? fullName : (user?.fullName || 'Not set')}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="e.g. Sarah Ahmed"
                    style={{
                      padding: '0.75rem 1rem',
                      background: isEditing ? 'var(--surface-2)' : 'transparent',
                      border: isEditing ? '1px solid var(--border)' : '1px solid transparent',
                      borderRadius: '8px',
                      color: 'var(--text-main)',
                      fontSize: '1rem',
                      transition: 'all 0.2s'
                    }}
                  />
                </div>

                {/* Username Slug (Immutable) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: '600' }}>System Username</label>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                      <Lock size={11} /> System slug (Fixed)
                    </span>
                  </div>
                  <input
                    disabled={true}
                    value={user?.username || ''}
                    style={{
                      padding: '0.75rem 1rem',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      color: 'var(--text-muted)',
                      fontSize: '1rem',
                      cursor: 'not-allowed'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Email Address */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: '600' }}>Email Address</label>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', overflow: 'hidden' }}>
                      <Mail size={16} color="var(--text-muted)" style={{ flexShrink: 0 }} />
                      <span style={{ color: 'var(--text-main)', fontSize: '0.95rem', wordBreak: 'break-all' }}>{user?.email || 'N/A'}</span>
                    </div>
                    <span style={{
                      fontSize: '0.72rem',
                      background: 'rgba(34, 197, 94, 0.1)',
                      color: '#22c55e',
                      padding: '0.15rem 0.5rem',
                      borderRadius: '12px',
                      fontWeight: 600,
                      flexShrink: 0
                    }}>
                      Verified
                    </span>
                  </div>
                </div>

                {/* Sign-in Method */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: '600' }}>Primary Auth Method</label>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                  }}>
                    <Shield size={18} color="var(--primary)" />
                    <span style={{ color: 'var(--text-main)', textTransform: 'capitalize', fontWeight: 500 }}>
                      {user?.authProvider || 'Email'}
                    </span>
                  </div>
                </div>
              </div>

              {isEditing && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                  <button
                    onClick={handleSaveProfile}
                    disabled={loading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      background: 'var(--primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      boxShadow: '0 4px 12px rgba(59,130,246,0.2)',
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    {loading ? <Loader size={18} className="deploy-spin" /> : <Save size={18} />}
                    {loading ? 'Saving...' : 'Save Profile Changes'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Connected VCS Accounts Section */}
          <div style={{ background: 'var(--surface)', borderRadius: '16px', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: '2rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
            <div style={{ padding: '1.5rem 2rem', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Link2 size={20} color="var(--primary)" /> Connected VCS Accounts
              </h3>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Connect your GitHub or GitLab accounts to deploy code directly from repositories.
              </p>
            </div>

            <div style={{ padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {gitActionSuccess && (
                <div style={{ padding: '0.75rem 1rem', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CheckCircle2 size={16} />
                  <span>{gitActionSuccess}</span>
                </div>
              )}

              {gitActionError && (
                <div style={{ padding: '0.75rem 1rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.2)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <AlertTriangle size={16} />
                    <span>{gitActionError}</span>
                  </div>
                  {gitActionError.toLowerCase().includes('password') && (
                    <button
                      type="button"
                      onClick={handleOpenPasswordModal}
                      style={{
                        padding: '0.35rem 0.85rem',
                        background: '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        transition: 'opacity 0.2s'
                      }}
                    >
                      Set Password Now
                    </button>
                  )}
                </div>
              )}

              {/* GitHub Connection Row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem', background: 'var(--surface-2)', borderRadius: '12px', border: '1px solid var(--border)', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: 44, height: 44, borderRadius: '10px', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      GitHub
                      {gitProvidersStatus.github ? (
                        <span style={{ fontSize: '0.72rem', background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', padding: '0.1rem 0.5rem', borderRadius: '12px', fontWeight: 600 }}>Connected</span>
                      ) : (
                        <span style={{ fontSize: '0.72rem', background: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8', padding: '0.1rem 0.5rem', borderRadius: '12px' }}>Not connected</span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                      {gitProvidersStatus.githubDetails?.email ? `Connected as ${gitProvidersStatus.githubDetails.email}` : 'Access your GitHub repositories for automated deployments'}
                    </div>
                  </div>
                </div>
                <div>
                  {gitProvidersStatus.github ? (
                    <button
                      type="button"
                      onClick={() => handleDisconnectProvider('github')}
                      disabled={gitActionLoading}
                      style={{ padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
                    >
                      Disconnect
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleConnectProvider('github')}
                      disabled={gitActionLoading}
                      style={{ padding: '0.5rem 1.25rem', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
                    >
                      Connect GitHub
                    </button>
                  )}
                </div>
              </div>

              {/* GitLab Connection Row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem', background: 'var(--surface-2)', borderRadius: '12px', border: '1px solid var(--border)', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: 44, height: 44, borderRadius: '10px', background: '#e24329', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                      <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 0 1-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 0 1 5.14 2h.02a.42.42 0 0 1 .4.28l2.44 7.51h8l2.44-7.51A.42.42 0 0 1 18.84 2h.02a.42.42 0 0 1 .4.28l2.44 7.51 1.22 3.78a.84.84 0 0 1-.27.94z" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      GitLab
                      {gitProvidersStatus.gitlab ? (
                        <span style={{ fontSize: '0.72rem', background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', padding: '0.1rem 0.5rem', borderRadius: '12px', fontWeight: 600 }}>Connected</span>
                      ) : (
                        <span style={{ fontSize: '0.72rem', background: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8', padding: '0.1rem 0.5rem', borderRadius: '12px' }}>Not connected</span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                      {gitProvidersStatus.gitlabDetails?.email ? `Connected as ${gitProvidersStatus.gitlabDetails.email}` : 'Access your GitLab repositories for automated deployments'}
                    </div>
                  </div>
                </div>
                <div>
                  {gitProvidersStatus.gitlab ? (
                    <button
                      type="button"
                      onClick={() => handleDisconnectProvider('gitlab')}
                      disabled={gitActionLoading}
                      style={{ padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
                    >
                      Disconnect
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleConnectProvider('gitlab')}
                      disabled={gitActionLoading}
                      style={{ padding: '0.5rem 1.25rem', background: '#e24329', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
                    >
                      Connect GitLab
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>



          <div style={{ background: 'var(--surface)', borderRadius: '16px', border: '1px solid var(--border)', padding: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-main)', margin: '0 0 1.5rem 0' }}>Security Settings</h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <button onClick={handleOpenEmailModal} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '12px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ padding: '0.5rem', background: 'var(--surface)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                    <Mail size={20} color="var(--primary)" />
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-main)', fontWeight: '600' }}>Email Address</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                      {user?.email || 'Not set'} · Primary address for security alerts and account recovery.
                    </div>
                  </div>
                </div>
                <span style={{ color: 'var(--primary)', fontWeight: '500', fontSize: '0.875rem' }}>Change Email</span>
              </button>

              <button onClick={handleOpenPasswordModal} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '12px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ padding: '0.5rem', background: 'var(--surface)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                    <Shield size={20} color="var(--primary)" />
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-main)', fontWeight: '600' }}>
                      {user?.passwordSet ? 'Change Password' : 'Set Account Password'}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                      {user?.passwordSet ? 'Update your password to keep your account secure.' : 'Add a password to sign in directly with email alongside OAuth.'}
                    </div>
                  </div>
                </div>
                <span style={{ color: 'var(--primary)', fontWeight: '500', fontSize: '0.875rem' }}>
                  {user?.passwordSet ? 'Update' : 'Set Password'}
                </span>
              </button>

              <button style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '12px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ padding: '0.5rem', background: 'var(--surface)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                    <Smartphone size={20} color="var(--success, #10b981)" />
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-main)', fontWeight: '600' }}>Two-Factor Authentication (2FA)</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Add an extra layer of security to your account.</div>
                  </div>
                </div>
                <span style={{ fontSize: '0.75rem', background: 'var(--surface-2)', color: 'var(--text-muted)', padding: '0.35rem 0.75rem', borderRadius: '2rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.25rem', border: '1px solid var(--border)' }}>
                  Disabled (Coming Soon)
                </span>
              </button>
            </div>
          </div>

          <div style={{ marginTop: '2rem', background: 'var(--surface)', borderRadius: '16px', border: '1px solid var(--border)', padding: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-main)', margin: '0 0 1.5rem 0' }}>Developer Settings</h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <button onClick={() => setShowWebhooks(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '12px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ padding: '0.5rem', background: 'var(--surface)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                    <Webhook size={20} color="var(--primary)" />
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-main)', fontWeight: '600' }}>API & Webhooks</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Automate deployments using our API and webhook endpoints.</div>
                  </div>
                </div>
                <ChevronRight size={18} color="var(--text-muted)" />
              </button>
            </div>
          </div>
        </>
      ) : (
        <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
          <button
            onClick={() => setShowWebhooks(false)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-main)', fontWeight: '500', marginBottom: '1.5rem', transition: 'all 0.2s' }}
          >
            <ArrowLeft size={16} /> Back to Profile Settings
          </button>
          <Webhooks user={user} onStatusMessage={onStatusMessage} />
        </div>
      )}

      {/* Change Email Modal */}
      {showEmailModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(8px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            animation: 'fadeIn 0.25s ease-out'
          }}
        >
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '20px',
              padding: '2rem',
              maxWidth: '460px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px var(--border)',
              position: 'relative'
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setShowEmailModal(false)}
              style={{
                position: 'absolute',
                top: '1.25rem',
                right: '1.25rem',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                padding: '0.4rem',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Close"
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: 'rgba(var(--primary-rgb), 0.12)',
                  border: '1px solid rgba(var(--primary-rgb), 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: 'var(--primary)'
                }}
              >
                <Mail size={26} />
              </div>
              <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-main)', letterSpacing: '-0.02em' }}>
                Change Email Address
              </h2>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.86rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
                Enter your new email address. A security notice will be sent to both your current and new inbox.
              </p>
            </div>

            {/* Current Email Indicator */}
            <div style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: '10px',
              padding: '0.75rem 1rem',
              marginBottom: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.85rem'
            }}>
              <span style={{ color: 'var(--text-muted)' }}>Current Email:</span>
              <span style={{ color: 'var(--text-main)', fontWeight: 600 }}>{user?.email || 'None'}</span>
            </div>

            {/* Error Banner */}
            {emailModalError && (
              <div
                style={{
                  padding: '0.75rem 1rem',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.25)',
                  borderRadius: '10px',
                  color: '#ef4444',
                  fontSize: '0.85rem',
                  marginBottom: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem'
                }}
              >
                <AlertCircle size={16} style={{ flexShrink: 0 }} />
                <span>{emailModalError}</span>
              </div>
            )}

            <form onSubmit={handleUpdateEmail} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                  New Email Address
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                  autoComplete="off"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: '10px',
                    color: 'var(--text-main)',
                    fontSize: '0.95rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Modal Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setShowEmailModal(false)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: '10px',
                    color: 'var(--text-main)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={emailLoading || !newEmail.trim()}
                  style={{
                    flex: 2,
                    padding: '0.75rem',
                    background: (emailLoading || !newEmail.trim()) ? 'var(--border)' : 'var(--primary)',
                    border: 'none',
                    borderRadius: '10px',
                    color: (emailLoading || !newEmail.trim()) ? 'var(--text-muted)' : '#ffffff',
                    fontWeight: 600,
                    cursor: (emailLoading || !newEmail.trim()) ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    boxShadow: (emailLoading || !newEmail.trim()) ? 'none' : '0 4px 14px rgba(var(--primary-rgb), 0.3)',
                    transition: 'all 0.2s'
                  }}
                >
                  {emailLoading ? <Loader size={16} className="deploy-spin" /> : null}
                  {emailLoading ? 'Updating...' : 'Update Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPasswordModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(8px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            animation: 'fadeIn 0.25s ease-out'
          }}
        >
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '20px',
              padding: '2rem',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px var(--border)',
              position: 'relative'
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setShowPasswordModal(false)}
              style={{
                position: 'absolute',
                top: '1.25rem',
                right: '1.25rem',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                padding: '0.4rem',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Close"
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: 'rgba(var(--primary-rgb), 0.12)',
                  border: '1px solid rgba(var(--primary-rgb), 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: 'var(--primary)'
                }}
              >
                <ShieldCheck size={28} />
              </div>
              <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-main)', letterSpacing: '-0.02em' }}>
                {user?.passwordSet ? 'Change Password' : 'Set Account Password'}
              </h2>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.86rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
                {user?.passwordSet
                  ? 'Update your account password with a strong, secure passphrase.'
                  : 'Set a password so you can sign in directly with your email and safely disconnect or manage your linked accounts.'}
              </p>
            </div>

            {/* Error Banner */}
            {pwdModalError && (
              <div
                style={{
                  padding: '0.75rem 1rem',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.25)',
                  borderRadius: '10px',
                  color: '#ef4444',
                  fontSize: '0.85rem',
                  marginBottom: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem'
                }}
              >
                <AlertCircle size={16} style={{ flexShrink: 0 }} />
                <span>{pwdModalError}</span>
              </div>
            )}

            <form onSubmit={handleChangePassword} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
              {/* New Password */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    autoComplete="new-password"
                    style={{
                      width: '100%',
                      padding: '0.75rem 2.8rem 0.75rem 1rem',
                      background: 'var(--surface-2, rgba(255,255,255,0.03))',
                      border: '1px solid var(--border)',
                      borderRadius: '10px',
                      color: 'var(--text-main)',
                      fontSize: '0.92rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      padding: 0
                    }}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                  Confirm Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    required
                    autoComplete="new-password"
                    style={{
                      width: '100%',
                      padding: '0.75rem 2.8rem 0.75rem 1rem',
                      background: 'var(--surface-2, rgba(255,255,255,0.03))',
                      border: confirmPassword && !doPwdsMatch ? '1px solid #ef4444' : '1px solid var(--border)',
                      borderRadius: '10px',
                      color: 'var(--text-main)',
                      fontSize: '0.92rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(p => !p)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      padding: 0
                    }}
                    title={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {confirmPassword && !doPwdsMatch && (
                  <span style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.3rem', display: 'block' }}>
                    Passwords do not match
                  </span>
                )}
              </div>

              {/* Real-time Password Security Rules */}
              <div
                style={{
                  background: 'var(--surface-2, rgba(255,255,255,0.02))',
                  padding: '0.9rem',
                  borderRadius: '10px',
                  border: '1px solid var(--border)',
                  fontSize: '0.8rem'
                }}
              >
                <div style={{ fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                  Password Requirements:
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.4rem' }}>
                  {[
                    { label: '8+ characters', met: pwdRules.length },
                    { label: '1 uppercase letter (A-Z)', met: pwdRules.upper },
                    { label: '1 lowercase letter (a-z)', met: pwdRules.lower },
                    { label: '1 number (0-9)', met: pwdRules.number },
                    { label: '1 special character (!@#$)', met: pwdRules.special }
                  ].map((rule, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        color: rule.met ? '#10b981' : 'var(--text-muted)'
                      }}
                    >
                      <CheckCircle2 size={13} style={{ flexShrink: 0 }} />
                      <span>{rule.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Modal Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: '10px',
                    color: 'var(--text-main)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={pwdLoading || !isPwdValid || !doPwdsMatch}
                  style={{
                    flex: 2,
                    padding: '0.75rem',
                    background: (!isPwdValid || !doPwdsMatch || pwdLoading) ? 'var(--border)' : 'var(--primary)',
                    border: '1px solid ' + ((!isPwdValid || !doPwdsMatch || pwdLoading) ? 'var(--border)' : 'var(--primary)'),
                    borderRadius: '10px',
                    color: (!isPwdValid || !doPwdsMatch || pwdLoading) ? 'var(--text-muted)' : '#ffffff',
                    fontWeight: 600,
                    cursor: (!isPwdValid || !doPwdsMatch || pwdLoading) ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    boxShadow: (!isPwdValid || !doPwdsMatch || pwdLoading) ? 'none' : '0 4px 14px rgba(var(--primary-rgb), 0.3)',
                    transition: 'all 0.2s'
                  }}
                >
                  {pwdLoading ? <Loader size={16} className="deploy-spin" /> : null}
                  {user?.passwordSet ? 'Update Password' : 'Set Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
